import { getContentConfig } from '../api/requests/player.requests.js';
import { PersistentCache } from '../offline/persistent_cache.js';
import { isConfigMissing } from '../utils/network_errors.js';
import { TransportFlags } from '../api/transport_flags.js';

export type AppVersionPlatformConfig = {
  hard: number;
  soft: number;
  store_url: string;
};

export type RateDialogConfig = {
  enabled: boolean;
  min_level: number;
  max_shows: number;
  repeat_after_levels: number;
  stop_after_rating: boolean;
  show_delay_ms: number;
};

export type BoosterHighlightConfig = {
  start_attempt: number;
  frequency: number;
  shots_limit: number;
};

export type AppRemoteConfig = {
  rate_dialog: RateDialogConfig;
  booster_highlight: BoosterHighlightConfig;
  menu_after_level: number;
  transport: {
    msgpack: boolean;
  };
  app_version: {
    android: AppVersionPlatformConfig;
    ios: AppVersionPlatformConfig;
  };
};

const STORE_URL_ANDROID = 'https://play.google.com/store/apps/details?id=com.matchstars.royalsmash';
const STORE_URL_IOS = 'https://apps.apple.com/app/id6789154721';

const DEFAULT_APP_CONFIG: AppRemoteConfig = {
  rate_dialog: {
    enabled: true,
    min_level: 8,
    max_shows: 1,
    repeat_after_levels: 10,
    stop_after_rating: true,
    show_delay_ms: 350,
  },
  booster_highlight: {
    start_attempt: 3,
    frequency: 3,
    shots_limit: 3,
  },
  menu_after_level: 0,
  transport: {
    msgpack: false,
  },
  app_version: {
    android: {
      hard: 0,
      soft: 0,
      store_url: STORE_URL_ANDROID,
    },
    ios: {
      hard: 0,
      soft: 0,
      store_url: STORE_URL_IOS,
    },
  },
};

function asRecord( value: unknown ): Record<string, unknown> | null
{
  return value != null && typeof value === 'object' && !Array.isArray( value )
    ? value as Record<string, unknown>
    : null;
}

function numberValue( value: unknown, fallback: number, min_value = 0 ): number
{
  const n = typeof value === 'number' ? value : Number( value );
  if ( !Number.isFinite( n ) ) return fallback;
  return Math.max( min_value, Math.floor( n ) );
}

function boolValue( value: unknown, fallback: boolean ): boolean
{
  if ( typeof value === 'boolean' ) return value;
  if ( typeof value === 'string' )
  {
    const normalized = value.trim().toLowerCase();
    if ( normalized === 'true' ) return true;
    if ( normalized === 'false' ) return false;
  }
  return fallback;
}

function stringValue( value: unknown, fallback: string ): string
{
  return typeof value === 'string' && value.trim().length > 0 ? value.trim() : fallback;
}

function normalizePlatformVersion( raw: unknown, fallback: AppVersionPlatformConfig ): AppVersionPlatformConfig
{
  const rec = asRecord( raw ) ?? {};
  return {
    hard: numberValue( rec.hard, fallback.hard ),
    soft: numberValue( rec.soft, fallback.soft ),
    store_url: stringValue( rec.store_url, fallback.store_url ),
  };
}

function normalizeAppConfig( raw: unknown, raw_booster: unknown, current_app_version: AppRemoteConfig['app_version'] ): AppRemoteConfig
{
  const root = asRecord( raw ) ?? {};
  const rate_dialog = asRecord( root.rate_dialog ) ?? {};
  const booster_highlight = asRecord( raw_booster ) ?? {};

  return {
    rate_dialog: {
      enabled: boolValue( rate_dialog.enabled, DEFAULT_APP_CONFIG.rate_dialog.enabled ),
      min_level: numberValue( rate_dialog.min_level, DEFAULT_APP_CONFIG.rate_dialog.min_level, 1 ),
      max_shows: numberValue( rate_dialog.max_shows, DEFAULT_APP_CONFIG.rate_dialog.max_shows, 1 ),
      repeat_after_levels: numberValue(
        rate_dialog.repeat_after_levels,
        DEFAULT_APP_CONFIG.rate_dialog.repeat_after_levels,
        1
      ),
      stop_after_rating: boolValue(
        rate_dialog.stop_after_rating,
        DEFAULT_APP_CONFIG.rate_dialog.stop_after_rating
      ),
      show_delay_ms: numberValue(
        rate_dialog.show_delay_ms,
        DEFAULT_APP_CONFIG.rate_dialog.show_delay_ms
      ),
    },
    booster_highlight: {
      start_attempt: numberValue( booster_highlight.start_attempt, DEFAULT_APP_CONFIG.booster_highlight.start_attempt, 1 ),
      frequency: numberValue( booster_highlight.frequency, DEFAULT_APP_CONFIG.booster_highlight.frequency, 1 ),
      shots_limit: numberValue( booster_highlight.shots_limit, DEFAULT_APP_CONFIG.booster_highlight.shots_limit, 1 ),
    },
    menu_after_level: numberValue( root.menu_after_level, DEFAULT_APP_CONFIG.menu_after_level, 0 ),
    transport: {
      msgpack: boolValue( ( asRecord( root.transport ) ?? {} ).msgpack, DEFAULT_APP_CONFIG.transport.msgpack ),
    },
    app_version: current_app_version,
  };
}

const APP_VERSION_CACHE_KEY = 'app_version_ws_cache';

function loadCachedAppVersion(): AppRemoteConfig['app_version']
{
  try
  {
    const raw = localStorage.getItem( APP_VERSION_CACHE_KEY );
    if ( !raw ) return DEFAULT_APP_CONFIG.app_version;
    const parsed = asRecord( JSON.parse( raw ) ) ?? {};
    return {
      android: normalizePlatformVersion( parsed.android, DEFAULT_APP_CONFIG.app_version.android ),
      ios: normalizePlatformVersion( parsed.ios, DEFAULT_APP_CONFIG.app_version.ios ),
    };
  }
  catch
  {
    return DEFAULT_APP_CONFIG.app_version;
  }
}

const APP_CONFIG_CACHE_KEY = 'app_config_v1';

interface CachedAppConfigs
{
  app: unknown;
  booster_highlight_config: unknown;
}

function loadCachedAppConfigs(): CachedAppConfigs | null
{
  const raw = PersistentCache.read<CachedAppConfigs>( APP_CONFIG_CACHE_KEY );
  return raw && typeof raw === 'object' ? raw : null;
}

export class AppConfigService
{
  private static cached = loadCachedAppConfigs();
  private static config: AppRemoteConfig = AppConfigService.cached
    ? normalizeAppConfig( AppConfigService.cached.app, AppConfigService.cached.booster_highlight_config, loadCachedAppVersion() )
    : { ...DEFAULT_APP_CONFIG, app_version: loadCachedAppVersion() };
  private static loaded = AppConfigService.cached !== null;
  private static loading: Promise<AppRemoteConfig> | null = null;

  static get(): AppRemoteConfig
  {
    return this.config;
  }

  static hasCachedConfig( type: string ): boolean
  {
    const cached = loadCachedAppConfigs();
    return cached !== null && type in cached;
  }

  static setAppVersion( android: unknown, ios: unknown = null ): void
  {
    const app_version = {
      android: normalizePlatformVersion( android, DEFAULT_APP_CONFIG.app_version.android ),
      ios: normalizePlatformVersion( ios, this.config.app_version.ios ),
    };
    this.config = { ...this.config, app_version };

    try
    {
      localStorage.setItem( APP_VERSION_CACHE_KEY, JSON.stringify( app_version ) );
    }
    catch ( e )
    {
      console.warn( '[AppConfig] failed to cache app_version', e );
    }
  }

  static async refresh(): Promise<AppRemoteConfig>
  {
    if ( this.loading ) return this.loading;

    this.loading = ( async () =>
    {
      try
      {
        const fetchPart = async ( type: string, current: unknown ): Promise<unknown> =>
        {
          try { return await getContentConfig<unknown>( type ); }
          catch ( e )
          {
            if ( isConfigMissing( e ) ) return null;
            console.warn( `[AppConfig] failed to fetch ${type}, keeping current`, e );
            return current;
          }
        };
        const [server_config, booster_config] = await Promise.all([
          fetchPart( 'app', this.cached?.app ?? null ),
          fetchPart( 'booster_highlight_config', this.cached?.booster_highlight_config ?? null ),
        ]);

        const merged: CachedAppConfigs = { app: server_config, booster_highlight_config: booster_config };
        this.config = normalizeAppConfig( merged.app, merged.booster_highlight_config, this.config.app_version );
        TransportFlags.setConfigDefault( this.config.transport.msgpack );
        this.loaded = true;
        this.cached = merged;
        PersistentCache.write( APP_CONFIG_CACHE_KEY, merged );
      }
      catch ( e )
      {
        if ( !this.loaded )
        {
          this.config = { ...DEFAULT_APP_CONFIG, app_version: this.config.app_version };
          TransportFlags.setConfigDefault( this.config.transport.msgpack );
        }
        console.warn( '[AppConfig] using defaults', e );
      }
      finally
      {
        this.loading = null;
      }

      return this.config;
    } )();

    return this.loading;
  }
}

TransportFlags.setConfigDefault( AppConfigService.get().transport.msgpack );
